/**
 * Profile selector — the parent's first stop after login.
 *
 * Single H1 ("Choose a profile"), one row per profile with a plain-English
 * status line + emerald check or amber alert, an inline "Connect YouTube"
 * pill when the session is signed out, and an empty state with the 3-step
 * desktop install flow. Status semantics match `ProfileLivenessPills` so the
 * row and the Account-menu dot pair stay in sync.
 *
 * Rows route to `/profile/:id` (the new ProfileHome) — not `/report` (gone).
 */
import React, {useEffect, useRef, useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {Link, useNavigate} from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ComputerRoundedIcon from '@mui/icons-material/ComputerRounded';
import _ from 'lodash';
import {onAuthStateChanged, User} from 'firebase/auth';
import {auth} from '../../firebase';
import {useGetMyYouTubeProfiles} from '../../api/graphql_profiles';
import {YouTubeProfile} from '../../types/generated/graphql';
import {usePageTitle} from '../../hooks/usePageTitle';
import {useAuthErrorHandler} from '../../hooks/useAuthErrorHandler';
import {ProfileSettingsDialog} from './ProfileSettingsDialog';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

const DESKTOP_FRESHNESS_MS = 120_000;

// Two avatar gradients cycled by index — they only exist so two siblings
// don't collide visually. Never load-bearing for identity (the YouTube
// avatar still wins when present).
const AVATAR_GRADIENTS: readonly string[] = [
  'linear-gradient(135deg, #FDA4AF 0%, #F43F5E 100%)',
  'linear-gradient(135deg, #A5B4FC 0%, #6366F1 100%)',
  'linear-gradient(135deg, #6EE7B7 0%, #10B981 100%)',
  'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
  'linear-gradient(135deg, #C4B5FD 0%, #8B5CF6 100%)',
];

interface ProfileStatus {
  youtubeConnected: boolean;
  workerActive: boolean;
  workerLastSeenMs: number | null;
  activeWorkerName: string | null;
}

function getProfileStatus(profile: YouTubeProfile): ProfileStatus {
  // Same derivation as ProfileLivenessPills — a `null` session valid is
  // treated optimistically (we may not have a desktop heartbeat yet) so
  // we fall back to the explicit `youtubeSignedOut` flag.
  const youtubeConnected =
    typeof profile.youtubeSessionValid === 'boolean'
      ? profile.youtubeSessionValid
      : profile.youtubeSignedOut === true
        ? false
        : true;
  // Worker liveness is now device-driven — any subscribed device
  // counts, not just the desktop. Falls back to the legacy
  // desktopLastSeenAt for back-compat when no devices are
  // subscribed yet (early users / pre-Phase-2 installs).
  const workers = profile.activeWorkers ?? [];
  const activeWorker = workers.find(w => w.isActive) ?? null;
  let workerLastSeenMs: number | null = null;
  for (const w of workers) {
    const t = Date.parse(w.lastSeenAt);
    if (!Number.isNaN(t)) {
      const ms = Date.now() - t;
      if (workerLastSeenMs == null || ms < workerLastSeenMs) {
        workerLastSeenMs = ms;
      }
    }
  }
  if (workerLastSeenMs == null && profile.desktopLastSeenAt) {
    const t = Date.parse(profile.desktopLastSeenAt);
    if (!Number.isNaN(t)) workerLastSeenMs = Date.now() - t;
  }
  const desktopActiveLegacy =
    workers.length === 0 &&
    workerLastSeenMs != null &&
    workerLastSeenMs < DESKTOP_FRESHNESS_MS;
  const workerActive = activeWorker != null || desktopActiveLegacy;
  return {
    youtubeConnected,
    workerActive,
    workerLastSeenMs,
    activeWorkerName: activeWorker?.name ?? null,
  };
}

function formatRelative(ms: number | null): string {
  if (ms == null) return 'never';
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) {
    const m = Math.floor(ms / 60_000);
    return `${m} min ago`;
  }
  if (ms < 86_400_000) {
    const h = Math.floor(ms / 3_600_000);
    return `${h} hr ago`;
  }
  const d = Math.floor(ms / 86_400_000);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

function StatusLine({status}: {status: ProfileStatus}): React.JSX.Element {
  if (status.youtubeConnected && status.workerActive) {
    const via = status.activeWorkerName ?? 'Desktop';
    return (
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <CheckCircleRoundedIcon sx={{fontSize: 16, color: '#10B981'}} />
        <Typography sx={{fontSize: 12, color: '#64748B'}}>
          Watching · synced via {via} {formatRelative(status.workerLastSeenMs)}
        </Typography>
      </Stack>
    );
  }
  if (!status.youtubeConnected) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <ErrorRoundedIcon sx={{fontSize: 16, color: '#F59E0B'}} />
        <Typography sx={{fontSize: 12, fontWeight: 500, color: '#B45309'}}>
          Not collecting — YouTube isn't connected on this profile
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <ErrorRoundedIcon sx={{fontSize: 16, color: '#F59E0B'}} />
      <Typography sx={{fontSize: 12, fontWeight: 500, color: '#B45309'}}>
        No worker app is running for this profile
      </Typography>
    </Stack>
  );
}

interface ProfileRowProps {
  profile: YouTubeProfile;
  index: number;
  onOpenSettings: (profile: YouTubeProfile) => void;
}

function ProfileRow({profile, index, onOpenSettings}: ProfileRowProps): React.JSX.Element {
  const navigate = useNavigate();
  const status = getProfileStatus(profile);
  const allGood = status.youtubeConnected && status.workerActive;
  const initial = (profile.name || '?').trim().charAt(0).toUpperCase() || '?';
  const tone = AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];

  const goToProfile = () => navigate(`/profile/${profile.id}`);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        px: 3,
        py: 2.5,
        cursor: 'pointer',
        transition: 'border-color 120ms, box-shadow 120ms',
        '&:hover': {
          borderColor: '#CBD5E1',
          boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.04)',
        },
      }}
      onClick={goToProfile}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          flexShrink: 0,
          borderRadius: '50%',
          background: tone,
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          fontWeight: 700,
          boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
          backgroundImage: profile.thumbnails?.[0]?.url
            ? `url(${profile.thumbnails[0].url})`
            : tone,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {!profile.thumbnails?.[0]?.url && initial}
      </Box>

      <Box sx={{flex: 1, minWidth: 0}}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: '#0F172A',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {profile.name || 'Untitled profile'}
          </Typography>
          {/* TODO(graphql): expose `age` on YouTubeProfile (today it
              lives on ProfilePreferences only). Until then we hide
              it on the row to avoid flashing "age —". */}
        </Stack>
        <Box sx={{mt: 0.5}}>
          <StatusLine status={status} />
        </Box>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1} sx={{flexShrink: 0}}>
        {!status.youtubeConnected && (
          <Button
            // The whole reason a profile shows "YouTube signed out"
            // is the desktop session cookie expired (or was never
            // set up). /welcome is the interactive first-run
            // checklist (Sign in → Install Desktop → Sign kid into
            // YouTube → Open dashboard), with per-step state
            // persisted on the user doc — the right destination for
            // a "walk me through reconnecting" intent. /setup is
            // the marketing-style how-to page; less useful here.
            component={Link}
            to="/welcome"
            variant="outlined"
            size="small"
            sx={{
              borderRadius: 999,
              borderColor: '#FCD34D',
              bgcolor: '#FFFBEB',
              color: '#B45309',
              fontWeight: 600,
              fontSize: 13,
              textTransform: 'none',
              px: 1.75,
              '&:hover': {bgcolor: '#FEF3C7', borderColor: '#FCD34D'},
            }}
            onClick={e => {
              // Stop the row's onClick from also firing (the row
              // routes to the profile dashboard); the Link's
              // navigation still proceeds.
              e.stopPropagation();
            }}
          >
            Connect YouTube
          </Button>
        )}
        <Button
          variant={allGood ? 'contained' : 'outlined'}
          size="small"
          endIcon={<ArrowForwardRoundedIcon sx={{fontSize: 16}} />}
          sx={{
            borderRadius: 999,
            fontWeight: 600,
            fontSize: 13,
            px: 2,
            textTransform: 'none',
            ...(allGood
              ? {bgcolor: '#3448F0', '&:hover': {bgcolor: '#2839C9'}}
              : {
                  borderColor: '#E2E8F0',
                  color: '#334155',
                  '&:hover': {
                    borderColor: '#A5B4FC',
                    bgcolor: '#EEF2FF',
                    color: '#3448F0',
                  },
                }),
          }}
          onClick={e => {
            e.stopPropagation();
            goToProfile();
          }}
        >
          Open
        </Button>
        <Tooltip title="Profile settings">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              onOpenSettings(profile);
            }}
            aria-label="Profile settings"
            sx={{color: '#94A3B8'}}
          >
            <SettingsRoundedIcon sx={{fontSize: 18}} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

function EmptyState(): React.JSX.Element {
  return (
    <>
      {/* Welcome-flow + setup-guide entry points. Sits above the
          existing 3-step instructions so a parent who skipped the
          /welcome checklist (or never saw it on a returning login)
          has a one-click path back to the guided flow. The detailed
          install steps below stay as the fallback. */}
      <Box
        sx={{
          mb: 2.5,
          borderRadius: 3,
          border: '1px solid #C7D2FE',
          bgcolor: '#EEF2FF',
          px: 3,
          py: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography sx={{fontSize: 14, color: '#1E2A9B', fontWeight: 600}}>
          New to GoodShowHQ?
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography
            component={Link}
            to="/welcome"
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: '#3448F0',
              textDecoration: 'none',
              '&:hover': {textDecoration: 'underline'},
            }}
          >
            Walk me through setup →
          </Typography>
          <Typography
            component={Link}
            to="/setup"
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: '#475569',
              textDecoration: 'none',
              '&:hover': {color: '#0F172A'},
            }}
          >
            Or read the full guide
          </Typography>
        </Stack>
      </Box>
    <Box
      sx={{
        borderRadius: 3,
        border: '2px dashed #E2E8F0',
        bgcolor: '#FFFFFF',
        p: 5,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          mx: 'auto',
          mb: 2.5,
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: '#EEF2FF',
          color: '#3448F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ComputerRoundedIcon sx={{fontSize: 22}} />
      </Box>
      <Typography sx={{fontSize: 18, fontWeight: 800, color: '#0F172A'}}>
        Connect your first profile
      </Typography>
      <Typography
        sx={{
          mx: 'auto',
          mt: 1,
          maxWidth: 420,
          fontSize: 13,
          lineHeight: 1.6,
          color: '#64748B',
        }}
      >
        GoodShowHQ watches your child's YouTube activity through a small agent
        that runs on your computer or mobile phone. Install it, sign your
        child in once, and their profile shows up here.
      </Typography>

      <Box sx={{mx: 'auto', mt: 3, maxWidth: 420, textAlign: 'left'}}>
        <Stack spacing={1.5}>
          {[
            "Download GoodShowHQ Desktop and install it on your child's computer.",
            'Open the app and sign your child in to YouTube the same way they normally would.',
            "Their profile will appear here within a minute. Refresh if it doesn't.",
          ].map((step, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: '50%',
                  bgcolor: '#0F172A',
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {i + 1}
              </Box>
              <Typography sx={{fontSize: 13, color: '#334155', lineHeight: 1.5}}>
                {step}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Stack
        direction={{xs: 'column', sm: 'row'}}
        spacing={1.5}
        justifyContent="center"
        alignItems="center"
        sx={{mt: 3.5}}
      >
        {/* TODO(download): swap `/download` for the canonical
            release URL once the marketing page exposes one. The
            placeholder route is intentional so QA can verify the
            click target is wired before the URL exists. */}
        <Button
          variant="contained"
          component={Link}
          to="/download"
          startIcon={<DownloadRoundedIcon sx={{fontSize: 18}} />}
          sx={{
            borderRadius: 999,
            bgcolor: '#3448F0',
            fontWeight: 700,
            fontSize: 13,
            px: 2.5,
            py: 1,
            textTransform: 'none',
            '&:hover': {bgcolor: '#2839C9'},
          }}
        >
          Download GoodShowHQ Desktop
        </Button>
        <Typography
          component={Link}
          to="/setup"
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: '#3448F0',
            textDecoration: 'none',
            '&:hover': {textDecoration: 'underline'},
          }}
        >
          Setup guide ↗
        </Typography>
      </Stack>
    </Box>
    </>
  );
}

function RowSkeleton({index}: {index: number}): React.JSX.Element {
  return (
    <Box
      key={`skeleton-${index}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        bgcolor: '#FFFFFF',
        px: 3,
        py: 2.5,
      }}
    >
      <Skeleton variant="circular" width={48} height={48} />
      <Box sx={{flex: 1}}>
        <Skeleton variant="text" width="40%" height={22} />
        <Skeleton variant="text" width="60%" height={16} />
      </Box>
      <Skeleton variant="rounded" width={80} height={32} />
    </Box>
  );
}

export default function ListProfiles({client}: Props): React.JSX.Element {
  usePageTitle('Profiles');

  const [user, setUser] = useState<User | null>(null);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u));
    return () => unsub();
  }, []);

  const {data, loading, error} = useGetMyYouTubeProfiles(client);
  useAuthErrorHandler(error);

  const [settingsForProfile, setSettingsForProfile] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const profiles: YouTubeProfile[] = _.compact(data?.getMyYouTubeProfiles ?? []);
  const hasProfiles = profiles.length > 0;

  // "Add profile" reveals the same install panel that the empty state
  // shows. We're under HashRouter, so a plain `href="#install-desktop"`
  // would corrupt the route hash and dump the user on a blank page.
  // Render the panel imperatively and scroll to it.
  const [showInstall, setShowInstall] = useState(false);
  const installRef = useRef<HTMLDivElement | null>(null);
  const revealInstall = () => {
    setShowInstall(true);
    // Defer the scroll so the panel is in the DOM before we measure it.
    setTimeout(() => {
      installRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, 0);
  };

  return (
    <Box sx={{minHeight: '100vh', bgcolor: '#F8FAFC'}}>
      <Container maxWidth="md" sx={{py: 6}}>
        {error && (
          <Card
            sx={{
              mb: 3,
              borderRadius: 2,
              borderLeft: '4px solid #DC2626',
              bgcolor: '#FEF2F2',
            }}
            elevation={0}
          >
            <CardContent>
              <Typography sx={{fontWeight: 600, color: '#991B1B'}}>
                Unable to load profiles
              </Typography>
              <Typography sx={{fontSize: 13, color: '#7F1D1D', mt: 0.5}}>
                {error.message}
              </Typography>
            </CardContent>
          </Card>
        )}

        {!user && !error && (
          <Card sx={{mb: 3}} elevation={0}>
            <CardContent>
              <Typography variant="h5" sx={{fontWeight: 800, color: '#0F172A'}}>
                Welcome to GoodShowHQ
              </Typography>
              <Typography sx={{fontSize: 14, color: '#64748B', mt: 1, mb: 2}}>
                Please sign in to access your profiles.
              </Typography>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/auth"
                fullWidth
                sx={{
                  borderRadius: 999,
                  bgcolor: '#3448F0',
                  '&:hover': {bgcolor: '#2839C9'},
                }}
              >
                Go to Sign In
              </Button>
            </CardContent>
          </Card>
        )}

        {user && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                mb: 4,
              }}
            >
              <Box>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: 24,
                    fontWeight: 800,
                    letterSpacing: '-0.01em',
                    color: '#0F172A',
                    lineHeight: 1.2,
                  }}
                >
                  Choose a profile
                </Typography>
                <Typography sx={{fontSize: 13, color: '#64748B', mt: 0.5}}>
                  {loading
                    ? 'Loading profiles…'
                    : hasProfiles
                      ? `${profiles.length} ${profiles.length === 1 ? 'profile' : 'profiles'} on this account.`
                      : 'Get started by connecting your first profile.'}
                </Typography>
              </Box>
              {hasProfiles && (
                <Button
                  variant="outlined"
                  startIcon={<AddRoundedIcon sx={{fontSize: 18}} />}
                  onClick={revealInstall}
                  sx={{
                    borderRadius: 999,
                    borderColor: '#E2E8F0',
                    color: '#334155',
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 2,
                    bgcolor: '#FFFFFF',
                    '&:hover': {borderColor: '#CBD5E1', bgcolor: '#F8FAFC'},
                  }}
                >
                  Add profile
                </Button>
              )}
            </Box>

            {loading ? (
              <Stack spacing={1.5}>
                {[0, 1, 2].map(i => (
                  <RowSkeleton key={i} index={i} />
                ))}
              </Stack>
            ) : hasProfiles ? (
              <Stack spacing={1.5}>
                {profiles.map((p, i) => (
                  <ProfileRow
                    key={p.id}
                    profile={p}
                    index={i}
                    onOpenSettings={prof =>
                      setSettingsForProfile({id: prof.id, name: prof.name})
                    }
                  />
                ))}
              </Stack>
            ) : (
              <Box id="install-desktop" ref={installRef}>
                <EmptyState />
              </Box>
            )}

            {hasProfiles && showInstall && (
              <Box id="install-desktop" ref={installRef} sx={{mt: 4}}>
                <EmptyState />
              </Box>
            )}

            {hasProfiles && (
              <Typography
                sx={{
                  mt: 5,
                  textAlign: 'center',
                  fontSize: 12,
                  color: '#94A3B8',
                }}
              >
                Want to add a child on a different computer?{' '}
                <Typography
                  component="a"
                  href="#install-desktop"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    revealInstall();
                  }}
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#3448F0',
                    textDecoration: 'none',
                    '&:hover': {textDecoration: 'underline'},
                  }}
                >
                  Install Desktop on it ↗
                </Typography>
              </Typography>
            )}
          </>
        )}

        <ProfileSettingsDialog
          client={client}
          profileId={settingsForProfile?.id ?? null}
          profileName={settingsForProfile?.name}
          open={settingsForProfile !== null}
          onClose={() => setSettingsForProfile(null)}
        />
      </Container>
    </Box>
  );
}
